import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import fetchWithCredentials from "apiClient";
import { useCondos } from "context/CondosContext";
import { useAuth } from "context/AuthContext";
import { Card } from "components/ui/Card";
import Spinner from "components/ui/Spinner";
import { MOBILE_PAGE_TITLE_MENU_INSET } from "constants/pageLayout";

interface DailySummary {
  id: number;
  condo: number;
  condoName: string;
  content: string;
}

export default function DailySummaries() {
  const { condos, loading: condosLoading } = useCondos();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = useCallback(
    (es: string, en: string) => (locale === "es" ? es : en),
    [locale],
  );

  // Initialize with yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  const [selectedCondoId, setSelectedCondoId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(getYesterdayDate());
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSummaries() {
      // Don't fetch if no date is selected
      if (!selectedDate) {
        setSummaries([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append("date", selectedDate);
        if (selectedCondoId) {
          params.append("condo", selectedCondoId);
        }

        const url = `/manager-api/daily-summaries/?${params.toString()}`;
        const data: DailySummary[] = await fetchWithCredentials(url);
        setSummaries(data);
      } catch (err) {
        console.error("Error loading daily summaries:", err);
        setError(tr("Error al cargar los resúmenes diarios", "Failed to load daily summaries"));
      } finally {
        setLoading(false);
      }
    }

    fetchSummaries();
  }, [selectedCondoId, selectedDate, tr]);

  const navigateDate = (direction: "prev" | "next") => {
    if (!selectedDate) return;

    const currentDate = new Date(selectedDate);
    currentDate.setDate(
      currentDate.getDate() + (direction === "next" ? 1 : -1)
    );
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  return (
    <div className="w-full h-full max-w-7xl overflow-y-auto bg-white py-4 max-lg:pl-0 max-lg:pr-4 lg:px-6">
      <h1
        className={`mb-6 text-2xl font-bold text-gray-900 ${MOBILE_PAGE_TITLE_MENU_INSET}`}
      >
        {tr("Resúmenes Diarios", "Daily Summaries")}
      </h1>

      <div className="max-lg:pl-4 lg:pl-0 space-y-6 lg:space-y-8">
      {/* Filters */}
      <div className="bg-white p-4 lg:p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tr("Edificio", "Building")}
            </label>
            <select
              value={selectedCondoId}
              onChange={(e) => setSelectedCondoId(e.target.value)}
              disabled={condosLoading}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">{tr("Todos los edificios", "All buildings")}</option>
              {condos.map((condo) => (
                <option key={condo.id} value={condo.id.toString()}>
                  {condo.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tr("Fecha", "Date")} <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateDate("prev")}
                disabled={!selectedDate}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={tr("Fecha anterior", "Previous date")}
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                required
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <button
                onClick={() => navigateDate("next")}
                disabled={!selectedDate}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={tr("Fecha siguiente", "Next date")}
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      ) : summaries.length === 0 ? (
        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
          <p className="text-sm text-gray-600">
            {tr(
              "No se encontraron resúmenes para los filtros seleccionados.",
              "No summaries found for the selected filters.",
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {summaries.map((summary) => (
            <Card
              key={summary.id}
              className="p-4 lg:p-6 border border-gray-200 shadow-sm"
            >
              <div className="mb-4 pb-3 border-b border-gray-200">
                <h3 className="text-l font-bold text-gray-900 mb-1">
                  {summary.condoName}
                </h3>
              </div>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {summary.content}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
